import { useState, useEffect, useMemo, useRef } from "react";
import { formatLocationLabel } from "./locationFilters";

const NOMINATIM_BASE = "https://nominatim.openstreetmap.org/search";
const DEBOUNCE_MS = 100;
const MIN_QUERY_LEN = 2;
const RESULT_LIMIT = 15;
const searchCache = new Map();

// These commonly searched places are available instantly. The geocoding
// request still supplements them with the full worldwide result set.
const INSTANT_LOCATION_OPTIONS = [
  "New York, NY, USA",
  "New Orleans, LA, USA",
  "Newark, NJ, USA",
  "New Haven, CT, USA",
  "New Braunfels, TX, USA",
  "Newport Beach, CA, USA",
  "New Westminster, British Columbia, Canada",
  "Newmarket, Ontario, Canada",
  "New Delhi, Delhi, India",
  "Newcastle, New South Wales, Australia",
  "Los Angeles, CA, USA",
  "San Francisco, CA, USA",
  "Chicago, IL, USA",
  "Toronto, Ontario, Canada",
  "Vancouver, British Columbia, Canada",
  "London, England, UK",
  "Dublin, Leinster, Ireland",
  "Sydney, New South Wales, Australia",
  "Singapore, Singapore",
];

const getInstantMatches = (query) => {
  const terms = String(query || "")
    .trim()
    .toLocaleLowerCase()
    .split(/\s+/)
    .filter(Boolean);
  if (terms.length === 0) return [];

  return INSTANT_LOCATION_OPTIONS.filter((location) => {
    const normalized = location.toLocaleLowerCase();
    return terms.every((term) => normalized.includes(term));
  });
};

const uniqueLabels = (values = []) => [...new Set(values.filter(Boolean))];

const NOMINATIM_COUNTRY_MAP = {
  us: "USA",
  ca: "Canada",
  gb: "UK",
};

const normalizeNominatimCountry = (country_code = "", country = "") => {
  const code = country_code.toLowerCase();
  return NOMINATIM_COUNTRY_MAP[code] || country || "";
};

const formatNominatimResult = (result) => {
  const addr = result.address || {};
  const city =
    addr.city ||
    addr.town ||
    addr.village ||
    addr.municipality ||
    addr.suburb ||
    addr.county ||
    "";
  const state = addr.state || "";
  const country = normalizeNominatimCountry(addr.country_code, addr.country);
  return formatLocationLabel(city, state, country);
};

/**
 * Debounced hook that queries the Nominatim (OpenStreetMap) geocoding API
 * and returns location suggestions formatted as "City, State, Country".
 *
 * Usage policy: Nominatim requires a valid Referer or User-Agent. The browser
 * automatically sends the Origin/Referer of this application.
 *
 * @param {string} query - The text the user has typed
 * @returns {{ results: string[], loading: boolean }}
 */
export default function useNominatimSearch(query) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef(null);
  const timerRef = useRef(null);
  const cleanedQuery = (query || "").trim();
  const instantResults = useMemo(() => getInstantMatches(cleanedQuery), [cleanedQuery]);

  useEffect(() => {
    const cleaned = cleanedQuery;
    if (cleaned.length < MIN_QUERY_LEN) {
      setResults([]);
      setLoading(false);
      return;
    }

    const cacheKey = cleaned.toLocaleLowerCase();
    const cachedResults = searchCache.get(cacheKey);
    if (cachedResults) {
      setResults(cachedResults);
      setLoading(false);
      return;
    }

    // Reuse a completed broader search while a narrower query is loading.
    const cachedPartialResults = uniqueLabels(
      [...searchCache.entries()]
        .filter(([cachedQuery]) => cacheKey.startsWith(cachedQuery))
        .flatMap(([, cachedValues]) => cachedValues)
        .filter((label) => label.toLocaleLowerCase().includes(cacheKey))
    );
    setResults(cachedPartialResults);

    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);
      try {
        const params = new URLSearchParams({
          q: cleaned,
          format: "json",
          addressdetails: "1",
          dedupe: "1",
          featuretype: "city",
          "accept-language": "en",
          limit: String(RESULT_LIMIT),
        });
        const res = await fetch(`${NOMINATIM_BASE}?${params}`, {
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`Nominatim error ${res.status}`);
        const data = await res.json();

        const labels = uniqueLabels(data.map(formatNominatimResult));
        searchCache.set(cacheKey, labels);
        setResults(labels);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.warn("Nominatim search error:", err);
        }
        setResults([]);
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timerRef.current);
    };
  }, [cleanedQuery]);

  // Abort pending request on unmount
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      clearTimeout(timerRef.current);
    };
  }, []);

  return { results: uniqueLabels([...instantResults, ...results]), loading };
}
